import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, API, fmtUSD } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [sites, setSites] = useState([]);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({ site_id: "", year: new Date().getFullYear(), quarter: Math.floor(new Date().getMonth() / 3) + 1 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [s, r] = await Promise.all([
      api.get("/sites").then((x) => x.data),
      api.get("/reports/list").then((x) => x.data),
    ]);
    setSites(s); setReports(r);
  };
  useEffect(() => { load(); }, []);

  const siteName = (id) => sites.find((s) => s.id === id)?.name || id;

  const generate = async () => {
    if (!form.site_id) { toast.error("Select site"); return; }
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/reports/quarterly/generate?site_id=${form.site_id}&year=${form.year}&quarter=${form.quarter}`,
        { method: "POST" }
      );
      if (!r.ok) throw new Error("Generation failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Q${form.quarter}_${form.year}_${siteName(form.site_id).replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report generated");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell
      title="Quarterly Site Reports"
      subtitle="Auto-generated branded PDFs — one click per site, per quarter"
    >
      <div className="bg-white border border-border rounded-md p-6 shadow-sm" data-testid="report-generator">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label>Site</Label>
            <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
              <SelectTrigger data-testid="report-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Year</Label>
            <Select value={String(form.year)} onValueChange={(v) => setForm({ ...form, year: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{[2023,2024,2025,2026].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quarter</Label>
            <Select value={String(form.quarter)} onValueChange={(v) => setForm({ ...form, quarter: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{[1,2,3,4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button disabled={loading} onClick={generate} data-testid="generate-report-btn" className="bg-[#F05D23] hover:bg-[#d94e18]">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> {loading ? "Generating..." : "Generate PDF"}
          </Button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-border rounded-md shadow-sm overflow-auto">
        <div className="px-5 py-4 border-b border-border"><h3 className="font-semibold">Report Archive</h3></div>
        <table className="w-full text-sm stryde-table">
          <thead>
            <tr><th className="text-left px-4 py-2.5">Site</th><th className="text-left px-4 py-2.5">Period</th><th className="text-left px-4 py-2.5">Generated</th><th className="text-right px-4 py-2.5">Revenue</th><th className="text-right px-4 py-2.5">Net</th><th className="text-right px-4 py-2.5">Outstanding AR</th></tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-2.5 font-semibold">{siteName(r.site_id)}</td>
                <td className="px-4 py-2.5">Q{r.quarter} {r.year}</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs">{new Date(r.generated_at).toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin">{fmtUSD(r.total_revenue)}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin font-semibold">{fmtUSD(r.stryde_net)}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin">{fmtUSD(r.outstanding_ar)}</td>
              </tr>
            ))}
            {reports.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No reports yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
